/**
 * UpdatePaymentMethodModal Component
 *
 * Modal dialog with Stripe Elements for updating the payment method on file.
 * Uses shared StripePaymentForm to support cards and ACH with billing details.
 */

import { useEffect, useRef, useState } from 'react';
import { AccountBalance, CheckCircle, Schedule } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import type { BillingDetails } from '@store/generated/subscriptions';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import {
  useCreateSetupIntentMutation,
  useUpdatePaymentMethodMutation,
} from '../store/api';

import { StripePaymentForm, type StripePaymentFormHandle } from './StripePaymentForm';

// Initialize Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string) || '');

/**
 * Verification pending state shown when micro-deposits are required
 */
function VerificationPendingContent({ onClose }: { onClose: () => void }) {
  return (
    <>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Schedule sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Bank Account Verification Pending
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We need to verify your bank account before it can be used for payments.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            What happens next:
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Two small deposits will be sent to your bank account"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="This usually takes 1-2 business days"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Check your email for verification instructions"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Alert>

        <Typography variant="body2" color="text.secondary">
          Once verified, your bank account will be automatically saved as your payment method.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Got It
        </Button>
      </DialogActions>
    </>
  );
}

interface UpdatePaymentMethodFormWrapperProps {
  onSuccess: () => void;
  onCancel: () => void;
  onVerificationPending: () => void;
  existingBillingDetails?: BillingDetails | null;
}

/**
 * Form wrapper that uses the shared StripePaymentForm
 */
function UpdatePaymentMethodFormWrapper({
  onSuccess,
  onCancel,
  onVerificationPending,
  existingBillingDetails,
}: UpdatePaymentMethodFormWrapperProps) {
  const formRef = useRef<StripePaymentFormHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updatePaymentMethod, { isLoading: isUpdating }] = useUpdatePaymentMethodMutation();

  const handleSubmit = async () => {
    if (!formRef.current) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Confirm payment with Stripe via shared form
      const paymentMethodId = await formRef.current.confirmPayment();

      // Update the payment method on our backend
      await updatePaymentMethod({
        updatePaymentMethodRequest: {
          payment_method_id: paymentMethodId,
        },
      }).unwrap();

      onSuccess();
    } catch (err) {
      // Error is already displayed by StripePaymentForm
      // Only set local error if it's from our API call
      if (err instanceof Error && err.message.includes('update')) {
        setError(err.message);
      }
      setIsSubmitting(false);
    }
  };

  const isProcessing = isSubmitting || isUpdating;

  return (
    <>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update your payment method and billing details below.
        </Typography>

        <StripePaymentForm
          ref={formRef}
          existingBillingDetails={existingBillingDetails}
          onVerificationPending={onVerificationPending}
          returnUrl={`${window.location.origin}/practice/subscriptions`}
          showProcessingIndicator={false}
        />

        {/* API Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isProcessing ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </DialogActions>
    </>
  );
}

export interface UpdatePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Optional existing billing details to pre-fill form */
  existingBillingDetails?: BillingDetails | null;
}

/**
 * Modal wrapper that fetches SetupIntent and provides Stripe context
 */
export function UpdatePaymentMethodModal({
  open,
  onClose,
  onSuccess,
  existingBillingDetails,
}: UpdatePaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [verificationPending, setVerificationPending] = useState(false);
  const [createSetupIntent, { isLoading: isCreatingIntent, error: intentError }] =
    useCreateSetupIntentMutation();

  // Fetch SetupIntent when modal opens
  useEffect(() => {
    if (open && !clientSecret && !verificationPending) {
      createSetupIntent()
        .unwrap()
        .then(data => {
          if (data.client_secret) {
            setClientSecret(data.client_secret);
          }
        })
        .catch(() => {
          // Error is handled via intentError
        });
    }
  }, [open, clientSecret, verificationPending, createSetupIntent]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setVerificationPending(false);
    }
  }, [open]);

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  const handleVerificationPending = () => {
    setVerificationPending(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance color="primary" />
        {verificationPending ? 'Verification Required' : 'Update Payment & Billing'}
      </DialogTitle>

      {/* Verification pending state */}
      {verificationPending && <VerificationPendingContent onClose={onClose} />}

      {/* Loading state while fetching SetupIntent */}
      {!verificationPending && isCreatingIntent && (
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Preparing secure form...</Typography>
          </Box>
        </DialogContent>
      )}

      {/* Error state */}
      {!verificationPending && intentError && (
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to initialize payment form. Please try again.
          </Alert>
          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setClientSecret(null);
                createSetupIntent();
              }}
            >
              Retry
            </Button>
          </DialogActions>
        </DialogContent>
      )}

      {/* Payment form once we have the client secret */}
      {!verificationPending && open && clientSecret && !intentError && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#1976d2',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              },
            },
          }}
        >
          <UpdatePaymentMethodFormWrapper
            onSuccess={handleSuccess}
            onCancel={onClose}
            onVerificationPending={handleVerificationPending}
            existingBillingDetails={existingBillingDetails}
          />
        </Elements>
      )}
    </Dialog>
  );
}
